import { useEffect, useState } from "react";
import {
  getFreeAccessDismissalKey,
  isFreeAccessCampaignActive,
  septemberFreeAccessCampaign,
} from "../campaigns/septemberFreeAccess";

const MAX_TIMEOUT_MS = 2147483647;

const readDismissal = (key) => {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "dismissed";
  } catch (_) {
    return false;
  }
};

export default function useFreeAccessAnnouncement({ dismissible, userId }) {
  const [now, setNow] = useState(Date.now);
  const key = dismissible ? getFreeAccessDismissalKey(userId) : null;
  const [dismissal, setDismissal] = useState(() => ({
    key,
    hidden: readDismissal(key),
  }));

  useEffect(() => {
    let timer;
    const refresh = () => {
      clearTimeout(timer);
      const currentTime = Date.now();
      setNow(currentTime);
      if (isFreeAccessCampaignActive(currentTime)) {
        timer = setTimeout(
          refresh,
          Math.min(
            septemberFreeAccessCampaign.expiresAt - currentTime,
            MAX_TIMEOUT_MS
          )
        );
      }
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  useEffect(() => {
    const refresh = () => setDismissal({ key, hidden: readDismissal(key) });
    refresh();
    if (!key) return undefined;
    const onStorage = (event) => {
      if (event.key === key || event.key === null) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key]);

  // Read the new identity immediately, before the effect runs after an account switch.
  const dismissed =
    dismissal.key === key ? dismissal.hidden : readDismissal(key);
  const dismiss = () => {
    if (!key) return;
    setDismissal({ key, hidden: true });
    try {
      localStorage.setItem(key, "dismissed");
    } catch (_) {
      // Dismiss for this mount even when the browser blocks persistence.
    }
  };

  return {
    visible:
      isFreeAccessCampaignActive(now) &&
      (!dismissible || Boolean(key)) &&
      !dismissed,
    dismiss,
  };
}
